import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { FormControlLabel, IconButton, TextField } from '@mui/material';
import { removeWhitespaceSpecialCharacters } from 'utils/stringUtil';
import ClearIcon from '@mui/icons-material/Clear';
import { jbColors } from 'theme/jbColors';
import { makeStyles } from '@mui/styles';
import { useTextfieldStyles } from 'components/Common/ValidatedTextField';

type TextFilterProps = {
  filterName: string;
  placeholderText: string;
  dispatchAction: any;
  defaultValue?: string;
  id?: string;
  customOnchange?: any;
  customKeyDownEvent?: any;
  debounceMs?: number;
  loading?: boolean;
  disabled?: boolean;
};

const useStyles = makeStyles(() => ({
  adornedEnd: {
    padding: 0,
    backgroundColor: jbColors.support.greenSmoke[10],
    '&:hover': {
      backgroundColor: jbColors.support.greenSmoke[10],
    },
  },
  iconButton: {
    padding: '1px',
    margin: '3px 5px',
    background: 'none',
    backgroundColor: jbColors.support.greenSmoke[10],
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.03)',
    },
  },
  closeIcon: {
    padding: '2px',
    color: jbColors.support.greenSmoke[80],
    '&:hover': {
      color: jbColors.support.greenSmoke[100],
    },
  },
}));

/**
 * A component that allows filtering the partner list via the text field input
 * Uses debouncing (only dispatches to Redux after the user stops typing)
 * @param filterName The name of the filter, displayed in the label
 * @param placeholderText The text to display if the field is empty
 * @param defaultValue The default value of the filter
 * @param dispatchAction The action to be executed,
 * @param id: the id of the textbox in the DOM
 * @param customOnchange: on change to be executed on change
 * @param loading: whether the text filter should indicate a circular progress bar for loading
 * @param debounceMs: time to dispatch the actions after. Default value is 500.
 */
export const TextFilter = ({
  filterName,
  placeholderText,
  defaultValue,
  dispatchAction,
  id,
  customOnchange,
  debounceMs = 500,
  disabled = false,
  loading = false,
}: TextFilterProps) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const textFieldClasses = useTextfieldStyles();
  const [value, setValue] = useState('');
  const [onChange$] = useState(new Subject<string>());

  const handleChange = (value: string) => {
    setValue(value);
    onChange$.next(value);
  };

  // defaultValue
  useEffect(() => {
    if (defaultValue) {
      setValue(defaultValue);
      onChange$.next(defaultValue);
    }
  }, [defaultValue, setValue, onChange$]);

  // debounce redux action
  const subscription = onChange$.pipe(debounceTime(debounceMs), distinctUntilChanged()).subscribe((val: any) => {
    dispatchAction(val);
  });

  useEffect(() => {
    return () => subscription.unsubscribe();
  }, [onChange$, dispatch, filterName]);

  return (
    <FormControlLabel
      classes={{
        root: textFieldClasses.labelRoot,
        labelPlacementTop: textFieldClasses.labelPlacementTop,
      }}
      control={
        <TextField
          id={id ?? `${removeWhitespaceSpecialCharacters(filterName)}-filter-input`}
          variant="filled"
          margin="dense"
          className={textFieldClasses.textInput}
          placeholder={placeholderText}
          disabled={disabled}
          value={value}
          autoComplete="false"
          inputProps={{
            autoComplete: 'off',
          }}
          onChangeCapture={(event: any) => (customOnchange ? customOnchange(event.currentTarget) : undefined)}
          onChange={(event) => handleChange(event.target.value)}
          InputProps={{
            disableUnderline: true,
            endAdornment: (
              <IconButton
                disableRipple
                disableFocusRipple
                about="test"
                className={classes.iconButton}
                onClickCapture={(event: any) => (customOnchange ? customOnchange('') : handleChange(''))}
                onClick={(event) => handleChange('')}
              >
                <ClearIcon className={classes.closeIcon} />
              </IconButton>
            ),
            classes: {
              adornedEnd: classes.adornedEnd,
            },
          }}
        />
      }
      label={filterName}
      labelPlacement="top"
    />
  );
};
